import { type MouseEvent, useReducer } from 'react';
import { Drawer } from '@mweb/zappos-ui/Drawer';
import type { TestableComponent } from '@mweb/types';

import type { ProductStyle } from 'types/cloudCatalog';
import type { NotificationParams, OutOfStockAction, OutOfStockProps, OutOfStockState } from 'components/productdetail/OutOfStock/OutOfStock.types';
import type { SelectedSizing } from 'types/product';
import type { FormattedProductBundle } from 'reducers/detail/productDetail';
import { Checkbox } from 'components/common/Checkbox/Checkbox';
import { useCustomerEmail, useProductStock } from 'components/productdetail/OutOfStock/OutOfStock.hooks';
import { OutOfStockContext, useOutOfStock } from 'components/productdetail/OutOfStock/OutOfStock.context';
import Image from 'components/common/Image';
import Button from 'components/common/Button';
import useWindowSize from 'hooks/useWindowSize';
import useMartyContext from 'hooks/useMartyContext';
import { isMobileViewport } from 'utils/viewportUtil';
import { getStockBySize, getStyleMap } from 'helpers/ProductUtils';
import { constructMSAImageUrl } from 'helpers/index';
import { trackError } from 'helpers/ErrorUtils';
import { INTERNET_EMAIL } from 'common/regex';
import BellSmallIcon from 'components/common/IconsV2/BellSmallIcon';
import { OutOfStockAltContainer } from 'components/productdetail/OutOfStock/OutOfStockAltContainer';

function reducer(state: OutOfStockState, action: OutOfStockAction): OutOfStockState {
  switch (action.type) {
    case 'OPEN_DRAWER':
      return { ...state, drawerStatus: 'open' };
    case 'CLOSE_DRAWER':
      return { ...state, drawerStatus: 'closed', formStatus: 'idle' };
    case 'UPDATE_EMAIL':
      return { ...state, email: action.payload };
    case 'TOGGLE_NEWSLETTER_SUBSCRIBE':
      return { ...state, hasOptedIntoNewsletter: !state.hasOptedIntoNewsletter };
    case 'SUBMIT_START':
      return { ...state, formStatus: 'submitting', error: null };
    case 'SUBMIT_SUCCESS':
      return { ...state, formStatus: 'success', drawerStatus: 'open' };
    case 'SUBMIT_ERROR':
      return { ...state, formStatus: 'error', error: action.payload };
    case 'RESET':
      return {
        ...state,
        drawerStatus: 'closed',
        formStatus: 'idle',
        error: null
      };
    default:
      return state;
  }
}

/**
 * Under the hood, this component fetches data from a few endpoints
 * - /v3/productBundle
 * - /mobileapi/v1/customerInfo
 */

function OutOfStockRoot({ children, colorId, selectedSizing, onSubmit, productId }: OutOfStockProps) {
  const { customerEmailAddress } = useCustomerEmail();
  const { stockData } = useProductStock(productId);

  const initialState: OutOfStockState = {
    drawerStatus: 'closed',
    formStatus: 'idle',
    email: '',
    hasOptedIntoNewsletter: true,
    error: null
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  // locate stock id for out-of-stock product
  const oosStock = getStockBySize(stockData, colorId, selectedSizing, true);
  const isLoggedIn = Boolean(customerEmailAddress);

  const notificationData: NotificationParams = {
    email: customerEmailAddress || state.email,
    // logged-in users don't have the ability to consent, so we only check logged-out users preference
    newsletter: isLoggedIn ? false : state.hasOptedIntoNewsletter,
    stockId: oosStock?.id
  };

  async function handleSubmit(event?: React.FormEvent) {
    if (event) {
      event.preventDefault();
    }

    dispatch({ type: 'SUBMIT_START' });

    try {
      await onSubmit(notificationData);

      dispatch({ type: 'SUBMIT_SUCCESS' });
    } catch (err) {
      trackError('Failed to submit out-of-stock notification', 'ERROR');
    }
  }

  const value = {
    state,
    dispatch,
    isLoggedIn,
    handleSubmit
  };

  return <OutOfStockContext.Provider value={value}>{children}</OutOfStockContext.Provider>;
}

function OutOfStockTrigger({ testId = 'notifyMeButton', disabled, ...rest }: React.ComponentProps<typeof Button> & TestableComponent) {
  const { state, dispatch, isLoggedIn, handleSubmit } = useOutOfStock();
  const { testId: getTestId } = useMartyContext();

  const isSubmitting = state.formStatus === 'submitting' && state.drawerStatus === 'closed';

  async function handleClick() {
    if (isLoggedIn) {
      // If logged in, submit directly with user's email fetched via useFetchAccountInfo
      await handleSubmit();
    }

    // Open drawer for both logged in and logged out users
    dispatch({ type: 'OPEN_DRAWER' });
  }

  return (
    <Button
      height="small"
      size="base"
      className="relative h-[40px] w-full" // height of 40px to match add to bag button
      data-test-id={getTestId(testId)}
      disabled={disabled || isSubmitting}
      onClick={handleClick}
      leadIcon={<BellSmallIcon className="[&>path]:stroke-[1.5]" size={16} />}
      {...rest}
    />
  );
}

function OutOfStockDrawer(props: Omit<React.ComponentProps<typeof Drawer>, 'position'>) {
  const { state, dispatch } = useOutOfStock();
  const { width } = useWindowSize();

  return (
    <Drawer
      // prevents stacking context issue with header
      contentClassName="z-30 focus:z-30"
      overlayClassName="z-30 focus:z-30"
      open={state.drawerStatus === 'open'}
      onOpenChange={() => dispatch({ type: 'CLOSE_DRAWER' })}
      position={isMobileViewport(width ?? 0) ? 'bottom' : 'right'}
      dataTestId="notifyMeDrawer"
      {...props}
    />
  );
}

interface OutOfStockFormProps extends TestableComponent {
  detail?: FormattedProductBundle;
  onStyleChange?: (e: MouseEvent<HTMLButtonElement>) => void;
  productName: string | undefined;
  productStyle: ProductStyle;
  selectedOptions: string;
  selectedSizing: SelectedSizing;
}

function OutOfStockForm({
  detail,
  onStyleChange,
  productName,
  productStyle,
  selectedOptions,
  selectedSizing,
  testId = 'notifyMeForm'
}: OutOfStockFormProps) {
  const { state, dispatch, handleSubmit } = useOutOfStock();
  const { testId: getTestId } = useMartyContext();

  const emailRegex = INTERNET_EMAIL.toString().slice(2, -2);

  const isSuccessful = state.formStatus === 'success';
  const isSubmitting = state.formStatus === 'submitting';
  const productStyles = !!detail && getStyleMap(detail.styles);

  if (isSuccessful) {
    return (
      <div className="flex flex-col gap-6" data-test-id={getTestId('notifyMeSuccess')}>
        <div className="flex flex-col items-center" data-test-id={getTestId('notifyMeSuccessMessage')}>
          <h3 className="text-xl">You're on the list!</h3>
          <p className="text-base">We'll let you know when this item is back in stock.</p>
        </div>

        <div className="flex flex-col items-center gap-2">
          <Image hasAspect className="min-h-32 max-w-40" src={constructMSAImageUrl(productStyle.imageId, { width: 300 })} />

          <div className="flex flex-col items-center text-pretty text-center">
            <span className="text-base" data-test-id={getTestId('notifyMeProductName')}>
              {productName}
            </span>
            <span className="text-base font-medium" data-test-id={getTestId('notifyMeSelectedSize')}>
              {selectedOptions}
            </span>
          </div>
        </div>

        {!!detail && !!productStyles && (
          <OutOfStockAltContainer detail={detail} onStyleChange={onStyleChange} productStyles={productStyles} selectedSizing={selectedSizing} />
        )}

        <Button
          onClick={() => dispatch({ type: 'CLOSE_DRAWER' })}
          className="w-full"
          height="small"
          data-test-id={getTestId('notifyMeSuccessClose')}
        >
          Close
        </Button>
      </div>
    );
  }

  return (
    <form method="POST" onSubmit={handleSubmit} className="flex flex-col gap-6" data-test-id={getTestId(testId)}>
      <div className="flex flex-col items-center gap-2" data-test-id={getTestId('productDetailContainer')}>
        <Image hasAspect className="min-h-32 max-w-40" src={constructMSAImageUrl(productStyle.imageId, { width: 300 })} />

        <div className="flex flex-col items-center text-pretty text-center">
          <span className="text-base" data-test-id={getTestId('notifyMeProductName')}>
            {productName}
          </span>
          <span className="text-base font-medium" data-test-id={getTestId('notifyMeSelectedSize')}>
            {selectedOptions}
          </span>
        </div>
      </div>

      <p className="text-base">Enter your email and we'll notify you when this style is back in stock.</p>

      <input
        type="email"
        name="email"
        value={state.email}
        pattern={emailRegex}
        onChange={e => dispatch({ type: 'UPDATE_EMAIL', payload: e.target.value })}
        required
        disabled={isSubmitting}
        placeholder="Enter Email"
        className="h-12 w-full rounded-lg border border-moderate bg-default-minimal px-4 py-2 text-base disabled:border-subtle disabled:bg-default-subtle disabled:text-disabled disabled:placeholder-disabled"
        data-test-id={getTestId('notifyMeInput')}
      />

      <Checkbox
        className="items-start"
        name="subscribe"
        label="Sign up for Zappos emails—new subscribers get 10% off a future order!* Plus, get early access to sales, coupons, and more."
        checked={state.hasOptedIntoNewsletter}
        onCheckedChange={() => dispatch({ type: 'TOGGLE_NEWSLETTER_SUBSCRIBE' })}
        disabled={isSubmitting}
        data-test-id={getTestId('subscribeCheckbox')}
      />

      {!!detail && !!productStyles && (
        <OutOfStockAltContainer detail={detail} onStyleChange={onStyleChange} productStyles={productStyles} selectedSizing={selectedSizing} />
      )}

      <Button
        type="submit"
        className="w-full"
        height="small"
        disabled={isSubmitting}
        leadIcon={state.formStatus === 'idle' && <BellSmallIcon className="[&>path]:stroke-[1.5]" size={16} />}
        data-test-id={getTestId('notifyMeSubmit')}
      >
        {isSubmitting ? 'Submitting...' : 'Notify Me'}
      </Button>

      <span className="text-xs italic">*One code per email address.</span>
    </form>
  );
}

export const OutOfStock = {
  Root: OutOfStockRoot,
  Trigger: OutOfStockTrigger,
  Drawer: OutOfStockDrawer,
  Form: OutOfStockForm
};
